import { useEffect } from 'react';

import { hasStorageAvailable } from '@klv/shared/utils/storage';

import type { ReactNode, RefObject } from 'react';
import type { IInitialFormValues, IHubSpotDataOrEmpty } from '../types';

export const ScrollToError = ({ children, innerRef }: { children: ReactNode; innerRef: RefObject<HTMLDivElement>; }) => {
  const errorRef = innerRef;
  useEffect(() => {
    if (errorRef && errorRef.current) {
      errorRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  });

  return <div ref={errorRef}>{children}</div>;
};

export const getNumMarketingFields = (initialValuesObject: object) => {
  const marketingFields = Object.keys(initialValuesObject).filter((key) => key.startsWith('field_'));
  const fieldNumbers = marketingFields.map((field) => Number(field.replace('field_', ''))).sort((a, b) => a - b);

  return fieldNumbers?.[fieldNumbers.length - 1];
};

export const getSessionStorageValues = () => {
  if (hasStorageAvailable('sessionStorage') && sessionStorage.getItem('formValues')) {
    const formValues = JSON.parse(sessionStorage.getItem('formValues') || '{}');
    return {
      email: formValues.email || '',
      full_name: formValues.full_name || '',
      field_6: '',
      field_9: '',
      field_10: true,
      field_14: '',
      company_website: formValues.company_website || formValues.company_url || '',
      company_url: formValues.company_url || formValues.company_website || '',
      company: formValues.company || '',
      phone: formValues.phone || '',
    };
  }
  return {
    email: '',
    full_name: '',
    field_6: '',
    field_9: '',
    field_10: true,
    field_14: '',
    company_website: '',
    company_url: '',
    company: '',
    phone: '',
  };
};

export const generateHBInitialValues = (data: IHubSpotDataOrEmpty, urlTrackingParams: any, storedTrackingParams: any, formState: any) => {
  const values: IInitialFormValues = {};
  if ('formFieldGroups' in data) {
    data.formFieldGroups.forEach((fieldGroup) => {
      fieldGroup.fields.forEach((field) => {
        if (urlTrackingParams.hasOwnProperty(field.name) || storedTrackingParams.hasOwnProperty(field.name)) {
          values[field.name] = urlTrackingParams?.[field.name as keyof typeof urlTrackingParams] || storedTrackingParams?.[field.name as keyof typeof storedTrackingParams] || '';
        } else if (storedTrackingParams.hasOwnProperty(field.name)) {
          values[field.name] = storedTrackingParams[field.name as keyof typeof storedTrackingParams];
        } else if (field.name === 'form_opt_in') {
          values[field.name] = Boolean(formState?.[field.name]) || false;
        } else {
          values[field.name] = formState?.[field.name] || '';
        }
      });
    });
    
    data.metaData.forEach((meta) => {
      if (meta.name === 'legalConsentOptions') {
        const parsedMeta = JSON.parse(meta.value);
        const communicationConsentCheckbox = parsedMeta.communicationConsentCheckboxes[0];
        const consentLanguage = communicationConsentCheckbox.label || '';
        const consentSubscriptionId = communicationConsentCheckbox.communicationTypeId || '';
        const checkboxLanguage = parsedMeta.processingConsentCheckboxLabel;
        
        values.consent_checkbox = formState?.consent_checkbox || '';
        values.consentCheckboxSubscriptionId = formState?.consentCheckboxSubscriptionId || consentSubscriptionId;
        values.consentCheckboxLanguage = formState?.consentCheckboxLanguage || checkboxLanguage;
        values.implicitConsentLanguage = formState?.implicitConsentLanguage || consentLanguage;
      }
    });
  }

  // added afterwards because this will never come from HubSpot
  values['g-recaptcha-response'] = '';

  return values;
};
